import React from 'react';
import { cn } from '@/lib/utils';

/**
 * Card surfaces. Text is navy on every light tint; muted text passes AA on sheet,
 * paper, sage and butter only (not lilac 4.46:1, not peach 3.86:1). Light tints are
 * `.site-light` (rust focus ring); `navy` is a dark card (sheet text, `.site-dark`
 * peach focus ring).
 */
export const SITE_CARD_TINTS = {
  sheet: 'site-light border-brand-rule bg-brand-sheet text-navy',
  paper: 'site-light border-brand-rule bg-brand-paper text-navy',
  peach: 'site-light border-brand-rule bg-brand-peach text-navy',
  sage: 'site-light border-brand-rule bg-brand-sage text-navy',
  lilac: 'site-light border-brand-rule bg-brand-lilac text-navy',
  butter: 'site-light border-brand-rule bg-brand-butter text-navy',
  navy: 'site-dark border-navy bg-navy text-brand-sheet',
};

/** A thicker coloured top edge (the reference's forms, panels and offer boxes). */
export const SITE_CARD_ACCENTS = {
  none: '',
  orange: 'border-t-4 border-t-brand-orange',
  rust: 'border-t-4 border-t-brand-rust',
  navy: 'border-t-4 border-t-navy',
};

export const SITE_CARD_PADDING = {
  md: 'p-6 nav:p-7',
  lg: 'p-[clamp(1.5625rem,4vw,3.5rem)]',
  none: '',
};

/**
 * A card: sheet surface, 1px rule border, square corners, a faint flat offset shadow.
 *
 * Props:
 * - `as`: element or component (default 'div'; 'article', 'li', a router Link, ...)
 * - `tint`: 'sheet' (default) | 'paper' | 'peach' | 'sage' | 'lilac' | 'butter' | 'navy'
 * - `accent`: 'none' (default) | 'orange' | 'rust' | 'navy' — 4px coloured top edge
 * - `shape`: 'square' (default) | 'discovery' (9px corners, clipped: image-led cards)
 * - `padding`: 'md' (default, 24 → 28px) | 'lg' (clamp(25px, 4vw, 56px)) | 'none'
 * - `interactive`: hover/focus-within deepens the shadow and, when motion is allowed,
 *   lifts the card 3px. Put the real link or button inside; the card is not a control.
 * - `className`, `children`, other props pass through.
 */
export default function SiteCard({
  as: Tag = 'div',
  tint = 'sheet',
  accent = 'none',
  shape = 'square',
  padding = 'md',
  interactive = false,
  className,
  children,
  ...rest
}) {
  return (
    <Tag
      className={cn(
        'relative border shadow-site-card',
        SITE_CARD_TINTS[tint] || SITE_CARD_TINTS.sheet,
        SITE_CARD_ACCENTS[accent] || '',
        shape === 'discovery' ? 'overflow-hidden rounded-discovery' : 'rounded-none',
        SITE_CARD_PADDING[padding] ?? SITE_CARD_PADDING.md,
        interactive && 'transition-[transform,box-shadow] duration-180 ease-out focus-within:shadow-site-card-hover hover:shadow-site-card-hover motion-safe:hover:-translate-y-[3px]',
        className,
      )}
      {...rest}
    >
      {children}
    </Tag>
  );
}
